//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { hashString } from '@/utils/Hash';

// Reading is pure, so the first render and a later identity change can share
// it -- the latter during render, which is what keeps the key and the value
// from ever being observed as a mismatched pair.
function readStored<T>(
  storageKey: string | null, sessionOnly: boolean, fallback: T,
  isValid?: (stored: unknown) => boolean
): T {
  if (!storageKey) return fallback;
  try {
    const stored = sessionOnly ? sessionStorage.getItem(storageKey) :
                                 localStorage.getItem(storageKey);
    if (stored === null) return fallback;
    const parsed: unknown = JSON.parse(stored);
    // Storage is the one input the app never writes alone: a half-finished
    // write, a hand edit or a value left by an older build all parse into
    // something of the wrong shape rather than failing. A caller that says
    // what it expects gets its default back instead of the surprise.
    return isValid && !isValid(parsed) ? fallback : parsed as T;
  } catch {
    return fallback;
  }
}

/**
 * Browser-side state, scoped to the signed-in user and persisted to storage.
 * This is the window-and-session-layout side of the split with
 * `useProfilePreference`: a value that should follow the person rather than
 * this browser belongs there, on the server's `UserProfile`, instead.
 */
export function usePersistentState<T>(
  key: string,
  defaultValue: T,
  sessionOnly = false,
  // What this caller will accept out of storage. Worth passing wherever a
  // wrong shape would do more than show a stale value -- a read during
  // render that throws takes the app down with it.
  isValid?: (stored: unknown) => boolean
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const { userProfile } = useAuth();

  // Derived during render rather than held in state, so every consumer below
  // sees one answer to "which user's bucket is this" -- a second copy can
  // disagree with this one, and an unscoped bucket is shared across users.
  // Null means there is nobody to scope storage to, which reads as the default
  // and writes nothing.
  const storageKey = (userProfile?.username && key) ?
                     `${hashString(userProfile.username)}-${key}` : null;

  // Held in a ref because callers pass object and array literals, which are a
  // fresh reference every render. Refreshed every render so a caller whose
  // default changes does not get the mount-time one on the next re-read.
  const fallback = useRef(defaultValue);
  fallback.current = defaultValue;

  const [value, setValue] = useState<T>(() =>
    readStored(storageKey, sessionOnly, defaultValue, isValid)
  );

  // Re-read the moment the identity or the storage medium changes, during
  // render rather than in an effect: React restarts the render before
  // committing, so no effect observes the new key beside the previous user's
  // value. Done in an effect instead, the write effect fires in the same
  // commit as the key change and persists the outgoing user's state under the
  // incoming user's key -- briefly, but to disk.
  const [loaded, setLoaded] = useState({ storageKey, sessionOnly });
  if (loaded.storageKey !== storageKey || loaded.sessionOnly !== sessionOnly) {
    setLoaded({ storageKey, sessionOnly });
    setValue(readStored(storageKey, sessionOnly, fallback.current, isValid));
  }

  // The key and the value change together in one render, so this needs no
  // guard of its own against writing a mismatched pair.
  useEffect(() => {
    if (!storageKey) return;
    try {
      const serialized = JSON.stringify(value);
      if (sessionOnly)
        sessionStorage.setItem(storageKey, serialized);
      else
        localStorage.setItem(storageKey, serialized);
    } catch {
      // Ignore.
    }
  }, [storageKey, value, sessionOnly]);

  // Track latest values in refs so the unmount cleanup can read them.
  const valueRef = useRef(value);
  const storageKeyRef = useRef(storageKey);
  valueRef.current = value;
  storageKeyRef.current = storageKey;

  // Wrap setValue so the ref is updated eagerly, before React renders.
  const setValueAndRef: React.Dispatch<React.SetStateAction<T>> =
    useCallback((action) => {
      setValue(prev => {
        const next = typeof action === 'function' ?
                     (action as (prev: T) => T)(prev) : action;
        valueRef.current = next;
        return next;
      });
    }, []);

  // Flush to storage on unmount.
  useEffect(() => {
    return () => {
      if (!storageKeyRef.current) return;
      try {
        const serialized = JSON.stringify(valueRef.current);
        if (sessionOnly)
          sessionStorage.setItem(storageKeyRef.current, serialized);
        else
          localStorage.setItem(storageKeyRef.current, serialized);
      } catch {
        // Ignore.
      }
    };
  }, [sessionOnly]);

  return [value, setValueAndRef];
}
